import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { 
  Users, UserPlus, Crown, Mail, Trash2, Settings, Copy, 
  Loader2, CheckCircle2, Clock, Shield, AlertTriangle, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('texhub_token') || sessionStorage.getItem('texhub_token')}`,
  'Content-Type': 'application/json'
});

const TeamPage = () => {
  const { user } = useAuth();
  const { isUSA } = useLocale();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [inviteDialog, setInviteDialog] = useState({ open: false });
  const [permissionsDialog, setPermissionsDialog] = useState({ open: false, member: null });
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [inviteLink, setInviteLink] = useState('');
  const [processing, setProcessing] = useState(false);

  const isAdmin = members.find(m => m.user_id === user?.user_id)?.is_admin;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [membersRes, invitationsRes, permissionsRes] = await Promise.all([
        fetch(`${API_URL}/api/team/members`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/team/invitations`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/team/permissions`, { headers: getAuthHeaders() })
      ]);

      if (membersRes.ok) setMembers(await membersRes.json());
      if (invitationsRes.ok) setInvitations(await invitationsRes.json());
      if (permissionsRes.ok) setPermissions(await permissionsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error(isUSA ? 'Enter an email' : 'Ingresa un email');
      return;
    }

    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/team/invite`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          email: inviteEmail,
          permissions: selectedPermissions
        })
      });

      const data = await res.json();
      
      if (res.ok) {
        const link = `${window.location.origin}/join/${data.token}`;
        setInviteLink(link);
        toast.success(isUSA ? 'Invitation created' : 'Invitación creada');
        fetchData();
      } else {
        toast.error(data.detail || (isUSA ? 'Error creating invitation' : 'Error al crear invitación'));
      }
    } catch (error) {
      toast.error(isUSA ? 'Error sending invitation' : 'Error al enviar invitación');
    } finally {
      setProcessing(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    toast.success(isUSA ? 'Link copied' : 'Enlace copiado');
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!confirm(isUSA ? 'Cancel this invitation?' : '¿Cancelar esta invitación?')) return;

    try {
      const res = await fetch(`${API_URL}/api/team/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        toast.success(isUSA ? 'Invitation cancelled' : 'Invitación cancelada');
        fetchData();
      } else {
        toast.error(isUSA ? 'Error cancelling' : 'Error al cancelar');
      }
    } catch (error) {
      toast.error(isUSA ? 'Error cancelling' : 'Error al cancelar');
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!confirm(isUSA ? `Remove ${memberName} from the team?` : `¿Remover a ${memberName} del equipo?`)) return;

    try {
      const res = await fetch(`${API_URL}/api/team/members/${memberId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        toast.success(isUSA ? 'Member removed' : 'Miembro removido');
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || (isUSA ? 'Error removing' : 'Error al remover'));
      }
    } catch (error) {
      toast.error(isUSA ? 'Error removing' : 'Error al remover');
    }
  };

  const handleUpdatePermissions = async () => {
    if (!permissionsDialog.member) return;

    setProcessing(true);
    try {
      const res = await fetch(`${API_URL}/api/team/members/${permissionsDialog.member.user_id}/permissions`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ permissions: selectedPermissions })
      });

      if (res.ok) {
        toast.success(isUSA ? 'Permissions updated' : 'Permisos actualizados');
        setPermissionsDialog({ open: false, member: null });
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || (isUSA ? 'Error updating' : 'Error al actualizar'));
      }
    } catch (error) {
      toast.error(isUSA ? 'Error updating' : 'Error al actualizar');
    } finally {
      setProcessing(false);
    }
  };

  const openInviteDialog = () => {
    setInviteEmail('');
    setSelectedPermissions(['view_catalog', 'view_quotes', 'view_messages', 'send_messages', 'view_stats', 'view_surplus']);
    setInviteLink('');
    setInviteDialog({ open: true });
  };

  const openPermissionsDialog = (member) => {
    setSelectedPermissions(member.permissions || []);
    setPermissionsDialog({ open: true, member });
  };

  const togglePermission = (permId) => {
    setSelectedPermissions(prev => 
      prev.includes(permId) 
        ? prev.filter(p => p !== permId)
        : [...prev, permId]
    );
  };

  if (!user?.company_id) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700 mb-2">
          {isUSA ? 'No registered company' : 'Sin empresa registrada'}
        </h2>
        <p className="text-slate-500">
          {isUSA ? 'You need to have a company to manage your team' : 'Necesitas tener una empresa para gestionar tu equipo'}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div data-testid="team-page">
      <div className="page-header">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="page-title flex items-center gap-2">
              <Users className="w-6 h-6" style={{ color: '#1E4C8E' }} />
              {isUSA ? 'My Team' : 'Mi Equipo'}
            </h1>
            <p className="page-subtitle">
              {isUSA 
                ? `${members.length} member${members.length !== 1 ? 's' : ''}`
                : `${members.length} miembro${members.length !== 1 ? 's' : ''}`}
              {invitations.length > 0 && (isUSA 
                ? ` • ${invitations.length} pending invitation${invitations.length !== 1 ? 's' : ''}`
                : ` • ${invitations.length} invitación${invitations.length !== 1 ? 'es' : ''} pendiente${invitations.length !== 1 ? 's' : ''}`)}
            </p>
          </div>
          {isAdmin && (
            <Button onClick={openInviteDialog} style={{ backgroundColor: '#34B772' }}>
              <UserPlus className="w-4 h-4 mr-2" />
              {isUSA ? 'Invite member' : 'Invitar miembro'}
            </Button>
          )}
        </div>
      </div>

      {/* Team Members */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            {isUSA ? 'Team Members' : 'Miembros del Equipo'}
          </CardTitle>
          <CardDescription>
            {isUSA ? 'Users with access to your company' : 'Usuarios con acceso a tu empresa'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {members.map((member) => (
              <div 
                key={member.user_id}
                className={`flex items-center justify-between p-4 rounded-lg border ${
                  member.is_admin ? 'bg-yellow-50 border-yellow-200' : 'bg-slate-50 border-slate-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    member.is_admin ? 'bg-yellow-100' : 'bg-blue-100'
                  }`}>
                    {member.is_admin ? (
                      <Crown className="w-5 h-5 text-yellow-600" />
                    ) : (
                      <Users className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900">
                        {member.full_name || member.email}
                      </p>
                      {member.is_admin && (
                        <Badge className="bg-yellow-100 text-yellow-700 text-xs">Admin</Badge>
                      )}
                      {member.user_id === user?.user_id && (
                        <Badge variant="outline" className="text-xs">{isUSA ? 'You' : 'Tú'}</Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{member.email}</p>
                    {!member.is_admin && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {member.permissions?.slice(0, 3).map(p => (
                          <Badge key={p} variant="outline" className="text-xs bg-white">
                            {permissions.find(perm => perm.id === p)?.label?.split(' ')[0] || p}
                          </Badge>
                        ))}
                        {member.permissions?.length > 3 && (
                          <Badge variant="outline" className="text-xs bg-white">
                            +{member.permissions.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {isAdmin && !member.is_admin && (
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => openPermissionsDialog(member)}
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      {isUSA ? 'Permissions' : 'Permisos'}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleRemoveMember(member.user_id, member.full_name || member.email)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {isAdmin && invitations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-600" />
              {isUSA ? 'Pending Invitations' : 'Invitaciones Pendientes'}
              <Badge className="bg-orange-100 text-orange-700">{invitations.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invitations.map((inv) => (
                <div 
                  key={inv.invitation_id}
                  className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{inv.email}</p>
                      <p className="text-sm text-slate-500">
                        {isUSA ? 'Expires: ' : 'Expira: '}{new Date(inv.expires_at).toLocaleDateString(isUSA ? 'en-US' : 'es-MX')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/join/${inv.token}`);
                        toast.success(isUSA ? 'Link copied' : 'Enlace copiado');
                      }}
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      {isUSA ? 'Copy link' : 'Copiar link'}
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleCancelInvitation(inv.invitation_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite Dialog */}
      <Dialog open={inviteDialog.open} onOpenChange={(open) => setInviteDialog({ open })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isUSA ? 'Invite Team Member' : 'Invitar Miembro al Equipo'}</DialogTitle>
            <DialogDescription>
              {isUSA ? 'The user will receive a link to join your company' : 'El usuario recibirá un enlace para unirse a tu empresa'}
            </DialogDescription>
          </DialogHeader>
          
          {!inviteLink ? (
            <>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>{isUSA ? "New member's email" : 'Email del nuevo miembro'}</Label>
                  <Input
                    type="email"
                    placeholder={isUSA ? 'email@example.com' : 'correo@ejemplo.com'}
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    {isUSA ? 'Permissions' : 'Permisos'}
                  </Label>
                  <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg max-h-48 overflow-y-auto">
                    {permissions.map((perm) => (
                      <div key={perm.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`perm-${perm.id}`}
                          checked={selectedPermissions.includes(perm.id)}
                          onCheckedChange={() => togglePermission(perm.id)}
                        />
                        <label 
                          htmlFor={`perm-${perm.id}`}
                          className="text-sm text-slate-700 cursor-pointer"
                        >
                          {perm.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setInviteDialog({ open: false })}>
                  {isUSA ? 'Cancel' : 'Cancelar'}
                </Button>
                <Button onClick={handleInvite} disabled={processing} style={{ backgroundColor: '#34B772' }}>
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : (isUSA ? 'Create invitation' : 'Crear invitación')}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="py-4">
              <div className="text-center mb-4">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                <p className="font-medium text-slate-900">{isUSA ? 'Invitation created!' : '¡Invitación creada!'}</p>
                <p className="text-sm text-slate-500">{isUSA ? `Share this link with ${inviteEmail}` : `Comparte este enlace con ${inviteEmail}`}</p>
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg">
                <Input value={inviteLink} readOnly className="bg-white text-sm" />
                <Button size="sm" onClick={handleCopyLink}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>

              <p className="text-xs text-slate-500 mt-2 text-center">
                {isUSA ? 'The link expires in 7 days' : 'El enlace expira en 7 días'}
              </p>

              <DialogFooter className="mt-4">
                <Button onClick={() => setInviteDialog({ open: false })} style={{ backgroundColor: '#34B772' }}>
                  {isUSA ? 'Done' : 'Listo'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={permissionsDialog.open} onOpenChange={(open) => setPermissionsDialog({ open, member: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isUSA ? 'Edit Permissions' : 'Editar Permisos'}</DialogTitle>
            <DialogDescription>
              {permissionsDialog.member?.full_name || permissionsDialog.member?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="grid grid-cols-2 gap-2 p-3 bg-slate-50 rounded-lg max-h-64 overflow-y-auto">
              {permissions.map((perm) => (
                <div key={perm.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`edit-perm-${perm.id}`}
                    checked={selectedPermissions.includes(perm.id)}
                    onCheckedChange={() => togglePermission(perm.id)}
                  />
                  <label 
                    htmlFor={`edit-perm-${perm.id}`}
                    className="text-sm text-slate-700 cursor-pointer"
                  >
                    {perm.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPermissionsDialog({ open: false, member: null })}>
              {isUSA ? 'Cancel' : 'Cancelar'}
            </Button>
            <Button onClick={handleUpdatePermissions} disabled={processing} style={{ backgroundColor: '#34B772' }}>
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : (isUSA ? 'Save changes' : 'Guardar cambios')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamPage;
